import React from "react";
import {
  UpdateObject,
  usePrimaryFetchedData,
} from "../../context/PrimaryFetchedDataContext";
import { useUserControls } from "../../context/UserControlsProvider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import { Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import Fab from "@mui/material/Fab";
import EditIcon from "@mui/icons-material/Edit";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import { ItemSnapshot, ItemStatus } from "../../types/DB2";
import { getStartingStatuses, getSuccessStatuses } from "../../util/util";
import { grey } from "@mui/material/colors";
import { Barcode } from "lucide-react";
import { useIonRouter } from "@ionic/react";
import ItemListControls from "./ItemListControls";
import OrderGroupsPanel from "./GroupPanel";

const AnimatedFab = styled(Fab, {
  shouldForwardProp: (prop) => prop !== "show",
})<{ show?: boolean }>`
  position: fixed;
  right: 1rem;
  bottom: max(1.25rem, calc(var(--mobile-bottom-toolbar-clearance, 0px) * 0.5 + 1rem));
  z-index: 1400;
  transition:
    opacity 200ms ease,
    transform 200ms ease;
  opacity: ${(p) => (p.show ? 1 : 0)};
  transform: ${(p) => (p.show ? "translateY(0)" : "translateY(8px)")};
  pointer-events: ${(p) => (p.show ? "auto" : "none")};
`;

const AllDoneWrapper = styled("div")`
  padding-top: 4rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

function groupItemsByOrderId(items: ItemSnapshot[]) {
  const groups: { [orderId: string]: ItemSnapshot[] } = {};
  items.forEach((item) => {
    const orderId = item.orderId || "Unknown Order";
    if (!groups[orderId]) groups[orderId] = [];
    groups[orderId].push(item);
  });
  return groups;
}

const CurrentWorkingList: React.FC = () => {
  // pull from context
  const { updateItemStatus, updateItemStatuses } = usePrimaryFetchedData();
  const { processedGroups, unprocessedGroups } = useUserControls();
  const {
    setAutoStartScanner,
    selectedShipmentIndex,
    loadingStatus,
    selectedShipmentObject,
  } = useUserControls();
  const router = useIonRouter();

  // state
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [actionsOpen, setActionsOpen] = React.useState(false);
  const [singleItemToUpdate, setSingleItemToUpdate] =
    React.useState<ItemSnapshot | null>(null);

  // derived
  const successStatuses = getSuccessStatuses(loadingStatus);
  const startStatuses = getStartingStatuses(loadingStatus);

  const processedItems = Object.values(processedGroups).flat();
  const unprocessedItems = Object.values(unprocessedGroups).flat();
  const allItems = processedItems.concat(unprocessedItems);
  const filteredItems = allItems;
  // Compute overall success across all included items and whether any are not processed
  const allItemsSuccess =
    allItems.length > 0 &&
    allItems.every((item: any) => successStatuses.includes(item.status));
  const anyNotProcessed = !(
    allItems.length > 0 && allItems.every((item: any) => item.currentProcessed)
  );
  // Panel visibility booleans
  const allItemsProcessed =
    allItems.length > 0 && allItems.every((item: any) => item.currentProcessed);
  const noItemsProcessed =
    allItems.length > 0 && allItems.every((item: any) => !item.currentProcessed);

  const allSelectedIdsProcessed = filteredItems
    .filter((item) => selectedIds.includes(item.id))
    .every((item) => item.currentProcessed);

  const handleSelect = (
    ids: string | string[],
    options?: { select?: boolean }
  ) => {
    const idArray = Array.isArray(ids) ? ids : [ids];
    const normalizedIds = Array.from(
      new Set(idArray.filter((id): id is string => typeof id === "string" && id.length > 0))
    );
    if (normalizedIds.length === 0) {
      try {
        console.warn("[CurrentWorkingList] handleSelect skipped (no valid ids)", {
          rawIds: ids,
          requestedState: options?.select,
        });
      } catch (_err) {}
      return;
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      try {
        console.debug("[CurrentWorkingList] handleSelect", {
          ids: normalizedIds,
          requestedState: options?.select,
          before: Array.from(next),
        });
      } catch (_err) {}
      if (typeof options?.select === "boolean") {
        if (options.select) {
          normalizedIds.forEach((id) => next.add(id));
        } else {
          normalizedIds.forEach((id) => next.delete(id));
        }
        return Array.from(next);
      }

      if (normalizedIds.length > 1) {
        const allPresent = normalizedIds.every((id) => next.has(id));
        if (allPresent) {
          normalizedIds.forEach((id) => next.delete(id));
        } else {
          normalizedIds.forEach((id) => next.add(id));
        }
        const result = Array.from(next);
        try {
          console.debug("[CurrentWorkingList] multi-select", {
            ids: normalizedIds,
            allPresent,
            after: result,
          });
        } catch (_err) {}
        return result;
      }

      const [singleId] = normalizedIds;
      if (next.has(singleId)) {
        next.delete(singleId);
      } else {
        next.add(singleId);
      }
      const result = Array.from(next);
      try {
        console.debug("[CurrentWorkingList] single-select", {
          id: singleId,
          after: result,
        });
      } catch (_err) {}
      return result;
    });
  };

  // handlers
  const handleUpdateStatuses = async (status: ItemStatus) => {
    if (selectedIds.length === 0) return;

    const itemUpdateObjects: UpdateObject[] = [];
    selectedIds.forEach((itemId) => {
      const item = filteredItems.find((item) => item.id === itemId);
      if (item) {
        itemUpdateObjects.push({
          itemId: item.id,
          orderId: item.orderId!,
          shipmentIndex: selectedShipmentIndex,
          newStatus: status,
          newProcessedState: true,
          updateMethod: "manual",
          prevStatus: item.status as ItemStatus,
        });
      }
    });
    await updateItemStatuses(itemUpdateObjects);
    setSelectedIds([]);
    setActionsOpen(false);
  };
  const handleUpdateStatus = async (status: ItemStatus) => {
    if (singleItemToUpdate) {
      await updateItemStatus({
        itemId: singleItemToUpdate.id,
        orderId: singleItemToUpdate.orderId!,
        newStatus: status,
        shipmentIndex: selectedShipmentIndex,
        newProcessedState: true,
        updateMethod: "manual",
        prevStatus: singleItemToUpdate.status as ItemStatus,
      });
      setSingleItemToUpdate(null);
    }
  };
  const handleResetItem = async (item: ItemSnapshot) => {
    await updateItemStatus({
      itemId: item.id,
      orderId: item.orderId!,
      newStatus: startStatuses[0],
      shipmentIndex: selectedShipmentIndex,
      newProcessedState: false,
      updateMethod: "manual",
      prevStatus: item.status as ItemStatus,
    });
  };
  const handleResetItems = async (items: ItemSnapshot[]) => {
    const itemUpdateObjects: UpdateObject[] = [];
    selectedIds.forEach((itemId) => {
      const item = items.find((item) => item.id === itemId);
      if (item) {
        itemUpdateObjects.push({
          itemId: item.id,
          shipmentIndex: selectedShipmentIndex,
          orderId: item.orderId!,
          newStatus: startStatuses[0],
          newProcessedState: false,
          updateMethod: "manual",
          prevStatus: item.status as ItemStatus,
        });
      }
    });
    await updateItemStatuses(itemUpdateObjects);
  };

  return (
    <>
      <List
        sx={{
          width: "100%",
          bgcolor: "background.paper",
          margin: 0,
          padding: 0,
        }}
      >
        {/* <ItemListControls /> */}
        {!noItemsProcessed && (
          <OrderGroupsPanel
            title={`${loadingStatus === "loading" ? "Loaded " : "Received "}(${processedItems.length}): `}
            isGroupsProcessed={true}
            allItemsSuccess={allItemsSuccess}
            anyNotProcessed={anyNotProcessed}
            groups={processedGroups}
            selectedIds={selectedIds}
            onToggleSelect={handleSelect}
            initiallyOpen={false}
            onUpdateStatusClick={(item) => {
              setSingleItemToUpdate(item);
              setActionsOpen(true);
            }}
            onResetItemClick={handleResetItem}
            successStatuses={successStatuses}
            startStatuses={startStatuses}
          />
        )}

        {!allItemsProcessed && (
          <OrderGroupsPanel
            title={`Pending (${unprocessedItems.length}): `}
            isGroupsProcessed={false}
            groups={unprocessedGroups}
            selectedIds={selectedIds}
            onToggleSelect={handleSelect}
            initiallyOpen
            onUpdateStatusClick={(item) => {
              setSingleItemToUpdate(item);
              setActionsOpen(true);
            }}
            onResetItemClick={(item) => {
              updateItemStatus({
                itemId: item.id,
                orderId: item.orderId!,
                newStatus: startStatuses[0],
                shipmentIndex: selectedShipmentIndex,
                newProcessedState: false,
              });
            }}
            successStatuses={successStatuses}
            startStatuses={startStatuses}
          />
        )}
        {noItemsProcessed && !!selectedShipmentObject && (
          <AllDoneWrapper
            onClick={() => {
              setAutoStartScanner(true);
              router.push("/tab2", "root");
            }}
          >
            <Barcode
              size="6rem"
              style={{ fontSize: "7rem", color: grey[400] }}
            />
            <Typography
              variant="h5"
              sx={{ padding: "1rem 0 .25rem 0", color: grey[400] }}
            >
              Scan an Item to Begin
            </Typography>
          </AllDoneWrapper>
        )}
        {allItemsProcessed && !!selectedShipmentObject && (
          <AllDoneWrapper>
            <CheckIcon style={{ fontSize: "7rem", color: grey[400] }} />
            <Typography
              variant="h5"
              sx={{ padding: "1rem 0 .25rem 0", color: grey[400] }}
            >
              All Items Received
            </Typography>
          </AllDoneWrapper>
        )}
        <div style={{ marginTop: "4rem" }} />
      </List>
      <AnimatedFab
        color="primary"
        aria-label="edit selected"
        onClick={() => setActionsOpen(true)}
        show={selectedIds.length > 0}
      >
        <EditIcon />
      </AnimatedFab>
      <SwipeableDrawer
        anchor="bottom"
        open={actionsOpen}
        onClose={() => {
          setTimeout(() => {
            setSingleItemToUpdate(null);
          }, 500);
          setActionsOpen(false);
        }}
        onOpen={() => setActionsOpen(true)}
        disableSwipeToOpen
      >
        <div style={{ padding: 8, maxHeight: "50vh", overflow: "auto" }}>
          <Typography variant="subtitle1" sx={{ px: 1, py: 0.5 }}>
            {singleItemToUpdate
              ? `Update Status for Item: ${singleItemToUpdate.id}`
              : `Update Status on ${selectedIds.length} Items`}
          </Typography>
          <List>
            {/* Replace these placeholder actions with your handlers */}
            {allSelectedIdsProcessed && !singleItemToUpdate && (
              <ListItem
                onClick={() => {
                  (async () => {
                    const allSelectedItems = filteredItems.filter((item) =>
                      selectedIds.includes(item.id)
                    );
                    await handleResetItems(allSelectedItems);
                    setActionsOpen(false);
                    setSelectedIds([]);
                  })();
                }}
              >
                <ListItemText primary={`Reset ${selectedIds.length} items`} />
              </ListItem>
            )}
            <ListItem
              onClick={() => {
                (async () => {
                  if (singleItemToUpdate) {
                    await handleUpdateStatus("damaged");
                  } else {
                    await handleUpdateStatuses("damaged");
                  }
                  setActionsOpen(false);
                })();
              }}
            >
              <ListItemText primary="Damaged" />
            </ListItem>
            <ListItem
              onClick={() => {
                (async () => {
                  if (singleItemToUpdate) {
                    await handleUpdateStatus("missing");
                  } else {
                    await handleUpdateStatuses("missing");
                  }
                  setActionsOpen(false);
                })();
              }}
            >
              <ListItemText primary="Missing" />
            </ListItem>
            <ListItem
              onClick={() => {
                (async () => {
                  if (singleItemToUpdate) {
                    await handleUpdateStatus("delivered");
                  } else {
                    await handleUpdateStatuses("delivered");
                  }
                  setActionsOpen(false);
                })();
              }}
            >
              <ListItemText primary="Delivered" />
            </ListItem>
          </List>
        </div>
      </SwipeableDrawer>
    </>
  );
};

export default CurrentWorkingList;
